import Skeleton from "./Skeleton.jsx";
import Pagination from "./Pagination.jsx";
import { DataTable, NoData, useMemo, useState } from "@";
import { LazyLoadComponent } from "react-lazy-load-image-component";
import React from "react";

const MemoizedPagination = React.memo(Pagination);

export default function Table({
  data,
  localPg,
  isSmall,
  striped,
  columns,
  isLoading,
  className,
  selectable,
  pageChange,
  pagination,
  noTableHead,
  perRowsChange,
  selectedChange,
  rowsPerPageOptions,
  selectableRowDisabled,
  initialRowsPerPage = 10,
  expandableRows,
  expandableRowsComponent,
  customRowRenderer,
  customStyles,
}) {
  // State for current page and rows per page
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(initialRowsPerPage);

  // Memoized paginated data
  const paginatedData = useMemo(() => {
    const start = (currentPage - 1) * rowsPerPage;
    const end = start + rowsPerPage;
    return data?.slice(start, end);
  }, [data, currentPage, rowsPerPage]);

  // Memoized columns
  const memoizedColumns = useMemo(() => columns, [columns]);

  // Memoized pagination component
  const memoizedPaginationComponent = useMemo(() => {
    return () => (
      <MemoizedPagination
        onPageChange={pageChange}
        totalRows={pagination?.rows}
        currentPage={pagination?.page}
        rowsPerPage={pagination?.size}
        onRowsPerPageChange={perRowsChange}
        rowsPerPageOptions={rowsPerPageOptions}
        totalPages={Math.ceil(pagination?.rows / pagination?.size)}
      />
    );
  }, [
    pageChange,
    perRowsChange,
    rowsPerPageOptions,
    pagination?.rows,
    pagination?.page,
    pagination?.size,
  ]);

  // Handle page change
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  // Handle rows per page change
  const handleRowsPerPageChange = (newRowsPerPage) => {
    setRowsPerPage(newRowsPerPage);
    setCurrentPage(1);
  };

  return (
    <>
      <LazyLoadComponent>
        <div
          className={`${className ? className : "relative -mx-3 mt-4 md:-mx-4 md:mt-5"}`}
        >
          {!isLoading ? (
            <>
              {localPg ? (
                <>
                  <DataTable
                    responsive
                    customStyles={customStyles}
                    highlightOnHover
                    striped={striped}
                    data={paginatedData}
                    columns={memoizedColumns}
                    noTableHead={noTableHead}
                    customRowRenderer={customRowRenderer}
                    selectableRows={selectable}
                    noDataComponent={<NoData />}
                    expandableRows={expandableRows}
                    onSelectedRowsChange={selectedChange}
                    selectableRowDisabled={selectableRowDisabled}
                    expandableRowsComponent={expandableRowsComponent}
                  />
                  <MemoizedPagination
                    localPg={localPg}
                    totalRows={data.length}
                    currentPage={currentPage}
                    rowsPerPage={rowsPerPage}
                    onPageChange={handlePageChange}
                    rowsPerPageOptions={rowsPerPageOptions}
                    onRowsPerPageChange={handleRowsPerPageChange}
                    totalPages={Math.ceil(data.length / rowsPerPage)}
                  />
                </>
              ) : (
                <DataTable
                  pagination
                  responsive
                  data={data}
                  paginationServer
                  highlightOnHover
                  paginationPerPage={100}
                  striped={striped}
                  noTableHead={noTableHead}
                  customStyles={customStyles}
                  columns={memoizedColumns}
                  customRowRenderer={customRowRenderer}
                  selectableRows={selectable}
                  noDataComponent={<NoData />}
                  expandableRows={expandableRows}
                  onSelectedRowsChange={selectedChange}
                  selectableRowDisabled={selectableRowDisabled}
                  paginationComponent={memoizedPaginationComponent}
                  expandableRowsComponent={expandableRowsComponent}
                />
              )}
            </>
          ) : (
            <Skeleton isSmall={isSmall} />
          )}
        </div>
      </LazyLoadComponent>
    </>
  );
}
